import React, { useState, useEffect, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import api from '../api/axios';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { XIcon, PlusIcon } from '../components/common/Icons';
import Pagination from '../components/common/Pagination';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import { checkRequired, apiFieldErrors, firstErrorMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const USER_LABELS = { username: "Nom d'utilisateur", password: 'Mot de passe', password_confirm: 'Confirmer', church_id: 'Église', church: 'Église', role: 'Rôle', email: 'Email', first_name: 'Prénom', last_name: 'Nom', phone: 'Téléphone' };

const ROLE_OPTIONS = [
  { value: 'superadmin_platform', label: 'Super Admin Plateforme' },
  { value: 'admin',        label: 'Administrateur' },
  { value: 'superviseur',  label: 'Superviseur multi-églises' },
  { value: 'responsable',  label: 'Responsable' },
  { value: 'accueil',      label: 'Accueil' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'chef_famille', label: 'Chef de Famille' },
];

// Les rôles "admin", "superadmin_platform" et "superviseur" (accès
// inter-églises) ne peuvent être attribués que par un super admin plateforme
// (même règle que côté backend).
const RESTRICTED_ROLES = ['admin', 'superadmin_platform', 'superviseur'];
const getRoleOptions = (currentUserRole) =>
  currentUserRole === 'superadmin_platform'
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter(opt => !RESTRICTED_ROLES.includes(opt.value));

const ROLE_COLORS = {
  superadmin_platform: { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  admin:        { bg: '#faf5ff', color: '#6b21a8', border: '#e9d5ff' },
  superviseur:  { bg: '#eef2ff', color: '#3730a3', border: '#c7d2fe' },
  responsable:  { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' },
  accueil:      { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  consultation: { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
  chef_famille: { bg: '#faf5ff', color: '#6b21a8', border: '#e9d5ff' },
};

const ROLE_LABELS = {
  superadmin_platform: 'Super Admin',
  admin:        'Administrateur',
  superviseur:  'Superviseur',
  responsable:  'Responsable',
  accueil:      'Accueil',
  consultation: 'Consultation',
  chef_famille: 'Chef de Famille',
};

const EMPTY_FORM = {
  username: '', email: '', first_name: '', last_name: '',
  role: '', phone: '', password: '', password_confirm: '',
  church_id: '', group_ids: [], supervised_church_ids: [],
};

const s = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' },
  btnPrimary: { display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 },
  btnSecondary: { backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '7px', padding: '9px 18px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 },
  btnDanger: { backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '7px', padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 },
  card: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' },
  badge: { display: 'inline-block', fontSize: '0.75rem', padding: '3px 10px', borderRadius: '999px', fontWeight: 600, border: '1px solid transparent' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal: { backgroundColor: 'white', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', marginBottom: '20px' },
  formGroup: { marginBottom: '13px' },
  formLabel: { display: 'block', fontSize: '0.84rem', fontWeight: 600, color: '#374151', marginBottom: '4px' },
  formInput: { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '0.95rem', color: '#1e293b', boxSizing: 'border-box' },
  formRow: { display: 'grid', gap: '12px' },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  actionButtons: { display: 'flex', gap: '8px', alignItems: 'center' },
  roleSelect: { padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.82rem', backgroundColor: 'white', cursor: 'pointer' },
};

export default function Administration() {
  const mob = useIsMobile();
  const { user } = useAuth();
  const { activeChurchId, isPlatformAdmin, canSwitchChurch, selectedChurch } = useChurch();

  const [users, setUsers]           = useState([]);
  const [churches, setChurches]     = useState([]);
  const [groups, setGroups]         = useState([]);
  const [openGroupMenu, setOpenGroupMenu] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetting, setResetting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;

  // Charger la liste des églises (superadmin : toutes ; superviseur : ses églises assignées)
  useEffect(() => {
    if (canSwitchChurch) {
      api.get('/churches/').then(r => setChurches(Array.isArray(r.data) ? r.data : r.data.results || [])).catch(() => {});
    }
  }, [canSwitchChurch]);

  // Charger la liste des groupes créés dans "Droits & groupes", pour pouvoir
  // les assigner directement ici sans changer de page.
  useEffect(() => {
    api.get('/auth/groups/?page_size=500').then(r => setGroups(Array.isArray(r.data) ? r.data : r.data.results || [])).catch(() => {});
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Superadmin/superviseur : filtrer par église active si sélectionnée
      const params = canSwitchChurch && activeChurchId ? { church_id: activeChurchId } : {};
      const res = await api.get('/auth/users/', { params: { ...params, page_size: PAGE_SIZE, page } });
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setUsers(data);
      const count = Array.isArray(res.data) ? data.length : (res.data.count ?? data.length);
      setTotalCount(count);
      setPageCount(Math.max(1, Math.ceil(count / PAGE_SIZE)));
    } catch {
      toast.error('Erreur lors du chargement des utilisateurs.');
    } finally {
      setLoading(false);
    }
  }, [canSwitchChurch, activeChurchId, page]);

  useEffect(() => { setPage(1); }, [canSwitchChurch, activeChurchId]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setForm({
      ...EMPTY_FORM,
      // Pré-remplir l'église active pour le superadmin/superviseur
      church_id: canSwitchChurch ? (activeChurchId || '') : (user?.church_id || ''),
    });
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = checkRequired(form, {
      username: "Nom d'utilisateur", password: 'Mot de passe', password_confirm: 'Confirmer',
      ...(canSwitchChurch && !['superadmin_platform', 'superviseur'].includes(form.role) ? { church_id: 'Église' } : {}),
      ...(form.role === 'superviseur' ? { supervised_church_ids: 'Églises supervisées' } : {}),
    });
    if (!errors.password && form.password.length < 6) errors.password = 'Le mot de passe doit contenir au moins 6 caractères.';
    if (!errors.password_confirm && form.password !== form.password_confirm) errors.password_confirm = 'Les mots de passe ne correspondent pas.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        username:         form.username,
        email:            form.email,
        first_name:       form.first_name,
        last_name:        form.last_name,
        phone:            form.phone,
        password:         form.password,
        password_confirm: form.password_confirm,
      };
      // Rôle optionnel : si non choisi, le backend applique son défaut
      // ('consultation') — l'utilisateur n'aura alors de droits que via
      // le(s) groupe(s) sélectionné(s) ci-dessous.
      if (form.role) payload.role = form.role;
      if (canSwitchChurch && !['superadmin_platform', 'superviseur'].includes(form.role)) payload.church_id = form.church_id;
      const res = await api.post('/auth/users/', payload);
      if (form.group_ids.length > 0) {
        await api.patch(`/auth/users/${res.data.id}/set_groups/`, { groups: form.group_ids });
      }
      if (form.role === 'superviseur' && form.supervised_church_ids.length > 0) {
        await api.patch(`/auth/users/${res.data.id}/set_supervised_churches/`, { church_ids: form.supervised_church_ids });
      }
      toast.success('Utilisateur créé avec succès.');
      setShowModal(false);
      setForm(EMPTY_FORM);
      fetchUsers();
    } catch (err) {
      const errors = apiFieldErrors(err, USER_LABELS);
      if (errors.church && !errors.church_id) errors.church_id = errors.church;
      setFormErrors(errors);
      toast.error(firstErrorMessage(errors, USER_LABELS, "Erreur lors de la création de l'utilisateur."));
    } finally {
      setSaving(false);
    }
  };

  const toggleUserGroup = async (targetUser, groupId) => {
    const current = (targetUser.groups || []).map(g => g.id);
    const next = current.includes(groupId) ? current.filter(id => id !== groupId) : [...current, groupId];
    try {
      const res = await api.patch(`/auth/users/${targetUser.id}/set_groups/`, { groups: next });
      setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, groups: res.data.groups } : u));
    } catch {
      toast.error('Erreur lors de la mise à jour des groupes.');
    }
  };

  const openResetPassword = (u) => {
    setResetErrors({});
    setResetTarget(u);
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetError('');
  };

  const [resetErrors, setResetErrors] = useState({});

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const errors = checkRequired({ resetPassword, resetPasswordConfirm },
      { resetPassword: 'Nouveau mot de passe', resetPasswordConfirm: 'Confirmer' });
    if (!errors.resetPassword && resetPassword.length < 6) errors.resetPassword = 'Le mot de passe doit contenir au moins 6 caractères.';
    if (!errors.resetPasswordConfirm && resetPassword !== resetPasswordConfirm) errors.resetPasswordConfirm = 'Les mots de passe ne correspondent pas.';
    setResetErrors(errors);
    setResetError('');
    if (Object.keys(errors).length) return;
    setResetting(true);
    try {
      await api.post(`/auth/users/${resetTarget.id}/change_password/`, { password: resetPassword });
      toast.success(`Mot de passe réinitialisé pour @${resetTarget.username}.`);
      setResetTarget(null);
    } catch (err) {
      setResetError(err.response?.data?.error || 'Erreur lors de la réinitialisation.');
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/auth/users/${userId}/`);
      toast.success('Utilisateur supprimé.');
      setUsers(prev => prev.filter(u => u.id !== userId));
      setConfirmDelete(null);
    } catch {
      toast.error("Erreur lors de la suppression de l'utilisateur.");
    }
  };

  // Colonnes selon le contexte
  const showChurchCol = isPlatformAdmin && !activeChurchId; // colonne Église si superadmin sans filtre
  const cols = mob
    ? '1.5fr 1.2fr 1fr'
    : showChurchCol ? '2fr 1.5fr 1.5fr 1.3fr 1.3fr 1fr' : '2fr 2fr 1.3fr 1.3fr 1fr';

  const selectedChurchName = isPlatformAdmin && activeChurchId
    ? (churches.find(c => String(c.id) === String(activeChurchId))?.name || selectedChurch?.name)
    : null;

  return (
    <div style={s.page}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>
            Utilisateurs{selectedChurchName ? ` — ${selectedChurchName}` : ''}
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px' }}>
            {isPlatformAdmin && !activeChurchId
              ? `${totalCount} utilisateur(s) sur toute la plateforme`
              : `${totalCount} utilisateur(s) dans cette église`
            }
          </p>
        </div>
        <button style={s.btnPrimary} onClick={openCreate}>
          <PlusIcon size={16} color="white" />
          Nouvel utilisateur
        </button>
      </div>

      {/* ── Info superadmin sans église ── */}
      {isPlatformAdmin && !activeChurchId && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.875rem', color: '#1e40af' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Sélectionnez une église dans la barre de navigation pour voir ou créer les utilisateurs d'une église spécifique.
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <LoadingSpinner size={36} label="Chargement..." />
        </div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          Aucun utilisateur trouvé.{isPlatformAdmin && !activeChurchId ? ' Sélectionnez une église pour voir ses utilisateurs.' : ''}
        </div>
      ) : (
        <div style={{ ...s.card, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '10px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: mob ? '420px' : 'auto' }}>
            <span>Utilisateur</span>
            {!mob && showChurchCol && <span>Église</span>}
            {!mob && <span>Email</span>}
            <span>Rôle / Groupe</span>
            {!mob && <span>Groupes</span>}
            <span>Actions</span>
          </div>
          {users.map((u, idx) => {
            const rc = ROLE_COLORS[u.role] || ROLE_COLORS.consultation;
            return (
              <div key={u.id} style={{ display: 'grid', gridTemplateColumns: cols, padding: '12px 20px', backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa', borderBottom: '1px solid #f1f5f9', alignItems: 'center', fontSize: '0.9rem', color: '#1e293b', minWidth: mob ? '420px' : 'auto' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>
                    {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.username}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>@{u.username}</div>
                  {mob && u.church_name && <div style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 600, marginTop: 2 }}>🏛 {u.church_name}</div>}
                </div>
                {!mob && showChurchCol && (
                  <div>
                    {u.church_name
                      ? <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e40af' }}>🏛 {u.church_name}</span>
                      : <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>
                    }
                  </div>
                )}
                {!mob && <div style={{ fontSize: '0.88rem', color: '#475569' }}>{u.email || '—'}</div>}
                <div>
                  <span style={{ ...s.badge, backgroundColor: rc.bg, color: rc.color, borderColor: rc.border }}>
                    {u.display_role || ROLE_LABELS[u.role] || u.role}
                  </span>
                </div>
                {!mob && (
                  <div style={{ position: 'relative' }}>
                    {u.role === 'superadmin_platform' ? (
                      <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>
                    ) : (
                      <>
                        <button type="button" onClick={() => setOpenGroupMenu(openGroupMenu === u.id ? null : u.id)}
                          style={{ ...s.roleSelect, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {(u.groups || []).length > 0 ? `${u.groups.length} groupe${u.groups.length > 1 ? 's' : ''}` : 'Aucun groupe'} ▾
                        </button>
                        {(u.groups || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                            {u.groups.map(g => (
                              <span key={g.id} style={{ fontSize: '0.68rem', fontWeight: 600, padding: '1px 7px', borderRadius: 999, background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>{g.name}</span>
                            ))}
                          </div>
                        )}
                        {openGroupMenu === u.id && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4, background: 'white', border: '1px solid #d1d5db', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 8, minWidth: 200 }}>
                            {groups.length === 0 ? (
                              <div style={{ fontSize: '0.78rem', color: '#94a3b8', padding: 4 }}>Aucun groupe créé.</div>
                            ) : groups.map(g => {
                              const checked = (u.groups || []).some(ug => ug.id === g.id);
                              return (
                                <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', fontSize: '0.82rem', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleUserGroup(u, g.id)} />
                                  {g.name}
                                </label>
                              );
                            })}
                            <button type="button" onClick={() => setOpenGroupMenu(null)} style={{ marginTop: 4, width: '100%', padding: '5px', fontSize: '0.76rem', background: '#f1f5f9', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#475569' }}>Fermer</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                <div style={s.actionButtons}>
                  <button style={s.btnSecondary} onClick={() => openResetPassword(u)}>
                    Réinitialiser mot de passe
                  </button>
                  {u.role !== 'superadmin_platform' && u.id !== user?.id && (
                    <button style={s.btnDanger} onClick={() => setConfirmDelete(u)}>
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Pagination page={page} pageCount={pageCount} totalCount={totalCount} pageSize={PAGE_SIZE} onChange={setPage} />

      {/* ── Modal création ── */}
      {showModal && (
        <div style={{ ...s.overlay, alignItems: mob ? 'flex-end' : 'center', padding: mob ? 0 : '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ ...s.modal, maxWidth: mob ? '100%' : '540px', borderRadius: mob ? '20px 20px 0 0' : '14px' }}>

            {/* En-tête modal avec indicateur église */}
            <div style={{ background: 'linear-gradient(135deg, #1e40af, #4338ca)', borderRadius: '10px', padding: '16px 18px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Créer un utilisateur</div>
                <div style={{ color: 'white', fontWeight: 800, fontSize: '1rem' }}>
                  {isPlatformAdmin
                    ? (form.church_id ? (churches.find(c => String(c.id) === String(form.church_id))?.name || 'Église sélectionnée') : 'Choisissez une église')
                    : (user?.church_name || 'Mon église')
                  }
                </div>
              </div>
              <button onClick={() => setShowModal(false)} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <XIcon size={16} />
              </button>
            </div>

            <form onSubmit={handleCreate} noValidate>
              {/* ── Sélecteur d'église (superadmin/superviseur, sauf pour créer un superadmin plateforme ou un superviseur, non rattachés à une seule église) ── */}
              {canSwitchChurch && !['superadmin_platform', 'superviseur'].includes(form.role) && (
                <div style={s.formGroup}>
                  <label style={s.formLabel}>
                    Église<Req /> <span style={{ color: '#94a3b8', fontWeight: 400 }}>(l'admin sera rattaché à cette église)</span>
                  </label>
                  <select
                    style={{ ...s.formInput, borderColor: formErrors.church_id ? ERROR_COLOR : '#d1d5db', backgroundColor: 'white' }}
                    value={form.church_id}
                    onChange={e => setForm({ ...form, church_id: e.target.value })}
                  >
                    <option value="">— Sélectionner une église —</option>
                    {churches.map(ch => (
                      <option key={ch.id} value={ch.id}>{ch.name}{ch.city ? ` · ${ch.city}` : ''}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.church_id} />
                </div>
              )}

              {/* ── Multi-sélection d'églises supervisées (rôle superviseur uniquement) ── */}
              {form.role === 'superviseur' && (
                <div style={s.formGroup}>
                  <label style={{ ...s.formLabel, color: formErrors.supervised_church_ids ? '#ef4444' : '#374151' }}>
                    Églises supervisées<Req /> <span style={{ color: '#94a3b8', fontWeight: 400 }}>(pourra basculer entre elles, mêmes droits qu'un admin sur chacune)</span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px', border: `1px solid ${formErrors.supervised_church_ids ? '#ef4444' : '#d1d5db'}`, borderRadius: '8px', background: 'white' }}>
                    {churches.map(ch => {
                      const selected = form.supervised_church_ids.includes(ch.id);
                      return (
                        <button
                          key={ch.id}
                          type="button"
                          onClick={() => setForm({
                            ...form,
                            supervised_church_ids: selected
                              ? form.supervised_church_ids.filter(id => id !== ch.id)
                              : [...form.supervised_church_ids, ch.id],
                          })}
                          style={{
                            padding: '5px 12px', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                            border: selected ? '1px solid #1e40af' : '1px solid #d1d5db',
                            background: selected ? '#dbeafe' : 'white',
                            color: selected ? '#1e40af' : '#374151',
                          }}
                        >
                          {ch.name}
                        </button>
                      );
                    })}
                    {churches.length === 0 && <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Aucune église disponible.</span>}
                  </div>
                  <FieldError message={formErrors.supervised_church_ids} />
                </div>
              )}

              {/* Prénom / Nom */}
              <div style={{ ...s.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Prénom</label>
                  <input style={s.formInput} value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="Jean" />
                </div>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Nom</label>
                  <input style={s.formInput} value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Kouassi" />
                </div>
              </div>

              {/* Username */}
              <div style={s.formGroup}>
                <label style={s.formLabel}>Nom d'utilisateur<Req /></label>
                <input
                  style={{ ...s.formInput, borderColor: formErrors.username ? ERROR_COLOR : '#d1d5db' }}
                  value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                  placeholder="jean.kouassi" autoComplete="off"
                />
                <FieldError message={formErrors.username} />
              </div>

              {/* Email / Téléphone */}
              <div style={{ ...s.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Email</label>
                  <input type="email" style={s.formInput} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jean@eglise.ci" />
                </div>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Téléphone</label>
                  <input style={s.formInput} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+225 07 XX XX XX" />
                </div>
              </div>

              {/* Rôle */}
              <div style={s.formGroup}>
                <label style={s.formLabel}>Rôle <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optionnel si un groupe est choisi ci-dessous)</span></label>
                <select style={{ ...s.formInput, backgroundColor: 'white' }} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  <option value="">— Aucun rôle (géré par groupe) —</option>
                  {getRoleOptions(user?.role).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div style={{ marginTop: 6, padding: '8px 12px', background: '#f8fafc', borderRadius: '7px', fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5 }}>
                  {form.role === 'admin' && '🔑 Accès complet à l\'église : membres, présences, activités, paramètres'}
                  {form.role === 'superviseur' && '🌐 Mêmes droits qu\'un administrateur, mais sur plusieurs églises — sélectionnez-les ci-dessous'}
                  {form.role === 'responsable' && '📋 Gestion des présences et des membres, pas les paramètres'}
                  {form.role === 'accueil' && '🚪 Pointage uniquement : scan QR et enregistrement de présences'}
                  {form.role === 'consultation' && '👁 Lecture seule : consultation sans modification'}
                  {form.role === 'chef_famille' && '👨‍👩‍👧 Voit uniquement les membres de sa famille — désignez-le ensuite dans Fidèles › Familles'}
                </div>
              </div>

              {/* Groupe(s) — droits précis en plus du rôle, gérés dans "Droits & groupes" */}
              {groups.length > 0 && (
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Groupe(s) <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optionnel — droits précis en plus du rôle)</span></label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {groups.map(g => {
                      const checked = form.group_ids.includes(g.id);
                      return (
                        <button key={g.id} type="button"
                          onClick={() => setForm(f => ({ ...f, group_ids: checked ? f.group_ids.filter(id => id !== g.id) : [...f.group_ids, g.id] }))}
                          style={{ padding: '6px 12px', borderRadius: 999, border: `1.5px solid ${checked ? '#1e40af' : '#d1d5db'}`, background: checked ? '#eff6ff' : 'white', color: checked ? '#1e40af' : '#475569', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                          {checked ? '✓ ' : ''}{g.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Mots de passe */}
              <div style={{ ...s.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Mot de passe<Req /></label>
                  <input type="password" autoComplete="new-password"
                    style={{ ...s.formInput, borderColor: formErrors.password ? ERROR_COLOR : '#d1d5db' }}
                    value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                  <FieldError message={formErrors.password} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Confirmer<Req /></label>
                  <input type="password" autoComplete="new-password"
                    style={{ ...s.formInput, borderColor: formErrors.password_confirm ? ERROR_COLOR : '#d1d5db' }}
                    value={form.password_confirm} onChange={e => setForm({ ...form, password_confirm: e.target.value })} />
                  <FieldError message={formErrors.password_confirm} />
                </div>
              </div>

              <div style={s.modalActions}>
                <button type="button" style={s.btnSecondary} onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" style={s.btnPrimary} disabled={saving}>
                  {saving ? 'Création...' : 'Créer l\'utilisateur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Réinitialisation mot de passe ── */}
      {resetTarget && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setResetTarget(null); }}>
          <div style={{ ...s.modal, maxWidth: '420px' }}>
            <div style={s.modalHeader}>
              <div style={{ ...s.modalTitle, marginBottom: 0 }}>Réinitialiser le mot de passe</div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px' }} onClick={() => setResetTarget(null)}>
                <XIcon size={20} />
              </button>
            </div>
            <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: '16px', marginTop: '4px' }}>
              Nouveau mot de passe pour <strong>@{resetTarget.username}</strong>.
            </p>
            <form onSubmit={handleResetPassword} noValidate>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Nouveau mot de passe<Req /></label>
                <input type="password" autoComplete="new-password" style={{ ...s.formInput, borderColor: resetErrors.resetPassword ? ERROR_COLOR : '#d1d5db' }}
                  value={resetPassword} onChange={e => setResetPassword(e.target.value)} />
                <FieldError message={resetErrors.resetPassword} />
              </div>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Confirmer<Req /></label>
                <input type="password" autoComplete="new-password" style={{ ...s.formInput, borderColor: resetErrors.resetPasswordConfirm ? ERROR_COLOR : '#d1d5db' }}
                  value={resetPasswordConfirm} onChange={e => setResetPasswordConfirm(e.target.value)} />
                <FieldError message={resetErrors.resetPasswordConfirm} />
              </div>
              {resetError && <div style={{ color: '#ef4444', fontSize: '0.82rem', marginBottom: '10px' }}>{resetError}</div>}
              <div style={s.modalActions}>
                <button type="button" style={s.btnSecondary} onClick={() => setResetTarget(null)}>Annuler</button>
                <button type="submit" style={s.btnPrimary} disabled={resetting}>
                  {resetting ? 'Enregistrement...' : 'Réinitialiser'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirmation suppression ── */}
      {confirmDelete && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div style={{ ...s.modal, maxWidth: '420px' }}>
            <div style={s.modalHeader}>
              <div style={{ ...s.modalTitle, marginBottom: 0 }}>Confirmer la suppression</div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px' }} onClick={() => setConfirmDelete(null)}>
                <XIcon size={20} />
              </button>
            </div>
            <p style={{ color: '#475569', fontSize: '0.95rem', marginBottom: '20px', marginTop: '12px' }}>
              Supprimer l'utilisateur <strong>@{confirmDelete.username}</strong>
              {confirmDelete.church_name ? ` (${confirmDelete.church_name})` : ''} ? Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button style={s.btnSecondary} onClick={() => setConfirmDelete(null)}>Annuler</button>
              <button style={{ ...s.btnDanger, padding: '9px 18px' }} onClick={() => handleDelete(confirmDelete.id)}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
